import type { TFunction } from 'i18next'
import { Frown, Meh, Smile } from 'lucide-react'
import { Sentiment } from '~/types/SentimentTypes'
import { FilterItem, FilterSection, SelectedFilterData } from '~/types/Shared'

import {
  CallDirection,
  CallFilterParams,
  CallsQueryParams,
  CallStatus,
  User,
  VoipProfile,
} from '@mangovoice/types'

import { CallsRouteQueryParametersRaw } from '../CallsRoutingTypes'

export interface DirectionItem extends FilterItem {
  title: string & ('Inbound' | 'Outbound' | 'Internal')
  data: CallDirection
}

export interface OutcomeItem extends FilterItem {
  title: string & ('Answered' | 'Missed')
  data: boolean | undefined
}

export interface SentimentItem extends FilterItem {
  title: 'Positive' | 'Neutral' | 'Negative'
  data: Sentiment
}

export interface StatusItem extends FilterItem {
  title: string & ('Failed' | 'In Progress' | 'Completed' | 'Canceled' | 'Busy')
  data: CallsQueryParams['statuses']
}

export interface TraitsItem extends FilterItem {
  title: string & 'Externally Transfered'
  data: boolean | undefined
}

export interface DurationItem extends FilterItem {
  title: string & ('10 seconds' | '1 minute' | '5 minutes' | '10 minutes')
  data:
    | CallsQueryParams['duration_in_seconds_gte']
    | CallsQueryParams['duration_in_seconds_lt']
}

export interface CallTraitItem extends FilterItem {
  title: (string & 'Voicemail Recorded') | 'Externally Transferred'
  data: boolean | undefined
}

export interface UserItem extends FilterItem {
  title: string
  data: number
}

export interface AiAgentItem extends FilterItem {
  title: string
  data: number
}

export const sectionIndices = {
  direction: 0,
  outcome: 1,
  aiAgent: 2,
  sentiment: 3,
  statuses: 4,
  callTraits: 5,
  duration: 6,
  user: 7,
}

export const getDefaultFilterData = (
  t: TFunction,
  callAnalyticsEnabled?: boolean,
): FilterSection[] => {
  const defaultFilterData = [
    {
      title: t('callFilters.sections.direction'),
      rows: [
        {
          items: [
            {
              title: t('callFilters.direction.inbound'),
              data: CallDirection.Inbound,
            },
            {
              title: t('callFilters.direction.outbound'),
              data: CallDirection.Outbound,
            },
            {
              title: t('callFilters.direction.internal'),
              data: CallDirection.Internal,
            },
          ] as DirectionItem[],
        },
      ],
    },
    {
      title: t('callFilters.sections.outcome', 'Outcome'),
      rows: [
        {
          items: [
            {
              title: t('callFilters.outcome.missed'),
              data: true,
            },
            {
              title: t('callFilters.outcome.answered'),
              data: false,
            },
          ] as OutcomeItem[],
        },
      ],
    },
    {
      title: t('callFilters.sections.aiAgent'),
      rows: [
        {
          items: [] as AiAgentItem[],
        },
      ],
    },
    {
      title: t('callFilters.sections.sentiment'),
      rows: [
        {
          title: t('callFilters.sentiment.office'),
          items: [
            {
              title: t('callFilters.sentiment.negative'),
              data: Sentiment.Negative,
              icon: Frown,
            },
            {
              title: t('callFilters.sentiment.neutral'),
              data: Sentiment.Neutral,
              icon: Meh,
            },
            {
              title: t('callFilters.sentiment.positive'),
              data: Sentiment.Positive,
              icon: Smile,
            },
          ] as SentimentItem[],
        },
        {
          title: t('callFilters.sentiment.patient'),
          items: [
            {
              title: t('callFilters.sentiment.negative'),
              data: Sentiment.Negative,
              icon: Frown,
            },
            {
              title: t('callFilters.sentiment.neutral'),
              data: Sentiment.Neutral,
              icon: Meh,
            },
            {
              title: t('callFilters.sentiment.positive'),
              data: Sentiment.Positive,
              icon: Smile,
            },
          ] as SentimentItem[],
        },
      ],
    },
    {
      title: t('callFilters.sections.status'),
      rows: [
        {
          items: [
            {
              title: t('callFilters.status.inProgress'),
              data: [CallStatus.InProgress],
            },
            {
              title: t('callFilters.status.completed'),
              data: [CallStatus.Completed],
            },
            {
              title: t('callFilters.status.failed'),
              data: [CallStatus.Failed],
            },
            {
              title: t('callFilters.status.canceled'),
              data: [CallStatus.Canceled],
            },
            {
              title: t('callFilters.status.busy'),
              data: [CallStatus.Busy],
            },
          ],
        },
      ],
    },
    {
      title: t('callFilters.sections.callTrait'),
      rows: [
        {
          items: [
            {
              title: t('callFilters.callTrait.voicemailRecorded'),
              data: true,
            },
            {
              title: t('callFilters.callTrait.externallyTransferred'),
              data: true,
            },
          ] as CallTraitItem[],
        },
      ],
    },
    {
      title: t('callFilters.sections.duration', 'Duration'),
      rows: [
        {
          title: t('callFilters.duration.longerThan', 'Longer Than'),
          items: [
            {
              title: t('callFilters.duration.tenSeconds'),
              data: 10,
            },
            {
              title: t('callFilters.duration.oneMinute'),
              data: 60,
            },
            {
              title: t('callFilters.duration.fiveMinutes'),
              data: 300,
            },
            {
              title: t('callFilters.duration.tenMinutes'),
              data: 600,
            },
          ] as DurationItem[],
        },
        {
          title: t('callFilters.duration.shorterThan', 'Shorter Than'),
          items: [
            {
              title: t('callFilters.duration.tenSeconds'),
              data: 10,
            },
            {
              title: t('callFilters.duration.oneMinute'),
              data: 60,
            },
            {
              title: t('callFilters.duration.fiveMinutes'),
              data: 300,
            },
            {
              title: t('callFilters.duration.tenMinutes'),
              data: 600,
            },
          ] as DurationItem[],
        },
      ],
    },
    {
      title: t('callFilters.sections.user'),
      rows: [
        {
          items: [] as UserItem[],
        },
      ],
    },
  ]
  if (callAnalyticsEnabled) {
    return [...defaultFilterData]
  } else {
    const sentimentTitle = t('callFilters.sections.sentiment')
    return [
      ...defaultFilterData.filter(
        (section) => section.title !== sentimentTitle,
      ),
    ]
  }
}

export const tryGetQueryFilters = (
  t: TFunction,
  selectedFilters: SelectedFilterData[] = [],
) => {
  const queryParams: CallFilterParams = {}

  selectedFilters.forEach((filter) => {
    const { sectionTitle, rowTitle, itemTitle, item } = filter

    const directionTitle = t('callFilters.sections.direction')
    const outcomeTitle = t('callFilters.sections.outcome', 'Outcome')
    const sentimentTitle = t('callFilters.sections.sentiment')
    const statusTitle = t('callFilters.sections.status')
    const callTraitTitle = t('callFilters.sections.callTrait')
    const durationTitle = t('callFilters.sections.duration', 'Duration')
    const userTitle = t('callFilters.sections.user')
    const aiAgentTitle = t('callFilters.sections.aiAgent')

    switch (sectionTitle) {
      case directionTitle:
        {
          const directionItem = item as DirectionItem
          queryParams.directions = queryParams.directions ?? []
          queryParams.directions.push(directionItem.data)
        }
        break
      case outcomeTitle:
        {
          const outcomeItem = item as OutcomeItem
          queryParams.is_missed = outcomeItem.data
        }
        break
      case sentimentTitle:
        {
          const sentimentRowTitle = rowTitle
          const sentimentItem = item as SentimentItem

          if (sentimentRowTitle === t('callFilters.sentiment.office')) {
            queryParams.office_overall_sentiment_score =
              queryParams.office_overall_sentiment_score ?? []
            queryParams.office_overall_sentiment_score.push(sentimentItem.data)
          } else if (sentimentRowTitle === t('callFilters.sentiment.patient')) {
            queryParams.customer_overall_sentiment_score =
              queryParams.customer_overall_sentiment_score ?? []
            queryParams.customer_overall_sentiment_score.push(
              sentimentItem.data,
            )
          }
        }
        break
      case statusTitle:
        {
          const statusItem = item as StatusItem
          if (!statusItem.data) {
            return
          }
          queryParams.statuses = queryParams.statuses ?? []
          queryParams.statuses = [...queryParams.statuses, ...statusItem.data]
        }
        break
      case callTraitTitle:
        {
          const traitItem = item as TraitsItem
          if (itemTitle === t('callFilters.callTrait.voicemailRecorded')) {
            queryParams.has_voicemail = traitItem.data
          } else if (
            itemTitle === t('callFilters.callTrait.externallyTransferred')
          ) {
            queryParams.is_externally_transferred = traitItem.data
          }
        }
        break
      case durationTitle:
        {
          const durationItem = item as DurationItem
          if (
            rowTitle === t('callFilters.duration.longerThan', 'Longer Than')
          ) {
            queryParams.duration_in_seconds_gte = durationItem.data
          } else if (
            rowTitle === t('callFilters.duration.shorterThan', 'Shorter Than')
          ) {
            queryParams.duration_in_seconds_lt = durationItem.data
          }
        }
        break
      case userTitle:
        {
          const userItem = item as UserItem
          queryParams.related_user_ids = queryParams.related_user_ids ?? []
          queryParams.related_user_ids.push(userItem.data)
        }
        break
      case aiAgentTitle:
        {
          const aiAgentItem = item as AiAgentItem
          queryParams.voip_profile_ids = queryParams.voip_profile_ids ?? []
          queryParams.voip_profile_ids.push(aiAgentItem.data)
        }
        break
      default:
        console.log(`Unknown Filter Section Title ${sectionTitle}`)
        break
    }
  })

  return queryParams
}
interface TryGetFilterDataFromQueryParamsParams {
  t: TFunction
  callsQueryParams: CallsRouteQueryParametersRaw
  callAnalyticsEnabled?: boolean
  pbxUsers?: User[]
  botProfiles?: VoipProfile[]
}
export const tryGetFilterDataFromQueryParams = ({
  t,
  callsQueryParams,
  callAnalyticsEnabled,
  pbxUsers,
  botProfiles,
}: TryGetFilterDataFromQueryParamsParams) => {
  const filterData = getDefaultFilterData(t, callAnalyticsEnabled)

  const directionTitle = t('callFilters.sections.direction')
  const outcomeTitle = t('callFilters.sections.outcome', 'Outcome')
  const sentimentTitle = t('callFilters.sections.sentiment')
  const statusTitle = t('callFilters.sections.status')
  const callTraitTitle = t('callFilters.sections.callTrait')
  const durationTitle = t('callFilters.sections.duration', 'Duration')
  const userTitle = t('callFilters.sections.user')
  const aiAgentTitle = t('callFilters.sections.aiAgent')
  const officeRowTitle = t('callFilters.sentiment.office')
  const patientRowTitle = t('callFilters.sentiment.patient')
  const longerThanRowTitle = t('callFilters.duration.longerThan', 'Longer Than')
  const shorterThanRowTitle = t(
    'callFilters.duration.shorterThan',
    'Shorter Than',
  )

  filterData.forEach((section) => {
    switch (section.title) {
      case directionTitle:
        if (callsQueryParams.dir) {
          for (let i = 0; i < section.rows[0].items.length; i++) {
            const item = section.rows[0].items[i] as DirectionItem
            item.isSelected = callsQueryParams.dir.includes(item.data)
          }
        }
        break
      case outcomeTitle:
        if (callsQueryParams.miss !== undefined) {
          const missedOutcomeItem = section.rows[0].items[0] as OutcomeItem
          const answeredOutcomeItem = section.rows[0].items[1] as OutcomeItem
          missedOutcomeItem.isSelected = Number(callsQueryParams.miss) == 1
          answeredOutcomeItem.isSelected = Number(callsQueryParams.miss) == 0
        }
        break
      case statusTitle:
        if (callsQueryParams.status) {
          for (let i = 0; i < section.rows[0].items.length; i++) {
            const item = section.rows[0].items[i] as StatusItem
            item.isSelected = callsQueryParams.status.includes(item.data![0])
          }
        }
        break
      case sentimentTitle:
        if (callsQueryParams.agent_sentiment) {
          const selectedSentiment = callsQueryParams.agent_sentiment
            .split(',')
            .map((sentiment) => sentiment.toLowerCase())
          const agentRow = section.rows.find(
            (row) => row.title === officeRowTitle,
          )
          if (agentRow) {
            const items = agentRow.items as SentimentItem[]
            items.forEach((item) => {
              item.isSelected = selectedSentiment.includes(
                item.data.toLowerCase(),
              )
            })
          }
        }

        if (callsQueryParams.customer_sentiment) {
          const selectedSentiment = callsQueryParams.customer_sentiment
            .split(',')
            .map((sentiment) => sentiment.toLowerCase())
          const callerRow = section.rows.find(
            (row) => row.title === patientRowTitle,
          )
          if (callerRow) {
            const items = callerRow.items as SentimentItem[]
            items.forEach((item) => {
              item.isSelected = selectedSentiment!.includes(item.data)
            })
          }
        }
        break
      case callTraitTitle:
        if (callsQueryParams.vmr !== undefined) {
          const voicemailItem = section.rows[0].items[0] as CallTraitItem
          voicemailItem.isSelected = Boolean(callsQueryParams.vmr)
        }

        if (callsQueryParams.trnsfr !== undefined) {
          const externallyTransferredItem = section.rows[0]
            .items[1] as CallTraitItem
          externallyTransferredItem.isSelected = Boolean(
            callsQueryParams.trnsfr,
          )
        }
        break
      case durationTitle:
        if (callsQueryParams.dur_gte) {
          const longerThanRow = section.rows.find(
            (row) => row.title === longerThanRowTitle,
          )
          if (longerThanRow) {
            for (let i = 0; i < longerThanRow.items.length; i++) {
              const item = longerThanRow.items[i] as DurationItem
              item.isSelected = Number(callsQueryParams.dur_gte) == item.data
            }
          }
        }

        if (callsQueryParams.dur_lt) {
          const shorterThanRow = section.rows.find(
            (row) => row.title === shorterThanRowTitle,
          )
          if (shorterThanRow) {
            for (let i = 0; i < shorterThanRow.items.length; i++) {
              const item = shorterThanRow.items[i] as DurationItem
              item.isSelected = Number(callsQueryParams.dur_lt) == item.data
            }
          }
        }
        break
      case userTitle:
        if (pbxUsers?.length) {
          const usersParam = `${callsQueryParams.u}`
          const userIds = usersParam?.split(',')
          section.rows[0].items =
            pbxUsers?.map(
              (u) =>
                ({
                  title: u.full_name ?? t('callFilters.user.unknown'),
                  data: u.id,
                  isDisabled: false,
                  isSelected: userIds.includes(String(u.id)),
                }) as UserItem,
            ) ?? []
        }
        break
      case aiAgentTitle:
        if (botProfiles?.length) {
          const vpParam = `${callsQueryParams.vp}`
          const vpIds = vpParam?.split(',')
          section.rows[0].items =
            botProfiles?.map(
              (bp) =>
                ({
                  title: bp.full_name ?? t('callFilters.aiAgent.unknown'),
                  data: bp.id,
                  isDisabled: false,
                  isSelected: vpIds.includes(String(bp.id)),
                }) as AiAgentItem,
            ) ?? []
        }
        break
      default:
        break
    }
  })

  // Remove AI Agent section if there are no bot profiles
  const filteredData = filterData.filter(
    (section) => section.title !== aiAgentTitle || botProfiles?.length,
  )

  return { filterData: filteredData }
}

export const tryGetSelectedFiltersFromFilterSections = (
  filterSections: FilterSection[],
): SelectedFilterData[] => {
  const selectedFilters: SelectedFilterData[] = []
  for (const section of filterSections) {
    const sectionTitle = section.title || ''
    for (const row of section.rows) {
      const rowTitle = row.title || ''
      for (const item of row.items) {
        const itemTitle = item.title || ''
        if (item.isSelected) {
          selectedFilters.push({
            sectionTitle,
            rowTitle,
            itemTitle,
            item,
          })
        }
      }
    }
  }

  return selectedFilters
}
